import { Toaster } from "@/components/ui/toaster"
import { Toaster as SonnerToaster } from "sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import NavigationTracker from '@/lib/NavigationTracker'
import PushNotificationManager from '@/components/PushNotificationManager';
import AuthDeepLinkManager from '@/components/AuthDeepLinkManager';
import AppUpdateGate from '@/components/AppUpdateGate';
import AppErrorBoundary from '@/components/AppErrorBoundary';
import GlobalErrorMonitor from '@/components/GlobalErrorMonitor';
import { pagesConfig } from './pages.config'
import { BrowserRouter, HashRouter, Route, Routes } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;
const isNativePlatform = typeof window !== 'undefined' && typeof window.Capacitor?.isNativePlatform === 'function'
  ? window.Capacitor.isNativePlatform()
  : false;
const Router = isNativePlatform ? HashRouter : BrowserRouter;

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;
const PerfilPage = Pages.Perfil;
const InscricaoEventoPage = Pages.InscricaoEvento;
const publicRegistrationPaths = [
  '/inscricao-evento',
  '/inscricao',
  '/inscricoes',
];

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <>
      <AuthDeepLinkManager />
      <PushNotificationManager />
      <Routes>
        <Route path="/" element={
          <LayoutWrapper currentPageName={mainPageKey}>
            <MainPage />
          </LayoutWrapper>
        } />
        {Object.entries(Pages).map(([path, Page]) => (
          <Route
            key={path}
            path={`/${path}`}
            element={
              <LayoutWrapper currentPageName={path}>
                <Page />
              </LayoutWrapper>
            }
          />
        ))}
        {PerfilPage && (
          <>
            <Route
              path="/auth"
              element={
                <LayoutWrapper currentPageName="Perfil">
                  <PerfilPage />
                </LayoutWrapper>
              }
            />
            <Route
              path="/perfil"
              element={
                <LayoutWrapper currentPageName="Perfil">
                  <PerfilPage />
                </LayoutWrapper>
              }
            />
          </>
        )}
        {InscricaoEventoPage && publicRegistrationPaths.map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <LayoutWrapper currentPageName="InscricaoEvento">
                <InscricaoEventoPage />
              </LayoutWrapper>
            }
          />
        ))}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </>
  );
};


function App() {

  return (
    <AppErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <AppUpdateGate>
            <Router>
              <NavigationTracker />
              <AuthenticatedApp />
            </Router>
            <GlobalErrorMonitor />
            <Toaster />
            <SonnerToaster richColors position="top-center" />
          </AppUpdateGate>
        </QueryClientProvider>
      </AuthProvider>
    </AppErrorBoundary>
  )
}

export default App
